import { useEffect, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type { Book } from '../domain/entities/types';
import {
  bookRepository,
  coverAssetRepository,
  goalRepository,
  integrityRepository,
  homeHighlightContentRepository,
  noteRepository,
  progressRepository,
  seriesRepository,
  sessionRepository,
  settingsRepository,
  statusHistoryRepository,
  quoteRepository
} from '../repositories/dexie/repositories';
import { statisticsService, type StatisticsPeriod } from '../services/statisticsService';
import { useActiveDatabaseRevision } from '../database/ActiveDatabaseContext';
import { homeHighlightService } from '../services/homeHighlightService';
import { settingsService } from '../services/settingsService';

export const useBooks = () => {
  const revision = useActiveDatabaseRevision();
  return useLiveQuery(() => bookRepository.list(), [revision], []);
};
export const useBook = (id?: string) => {
  const revision = useActiveDatabaseRevision();
  return useLiveQuery(() => id ? bookRepository.get(id) : undefined, [revision, id]);
};
export const useSessions = (bookId?: string) => {
  const revision = useActiveDatabaseRevision();
  return useLiveQuery(() => bookId ? sessionRepository.listByBook(bookId) : [], [revision, bookId], []);
};
export const useSession = (id?: string) => {
  const revision = useActiveDatabaseRevision();
  return useLiveQuery(() => id ? sessionRepository.get(id) : undefined, [revision, id]);
};
export const useNotes = (bookId?: string) => {
  const revision = useActiveDatabaseRevision();
  return useLiveQuery(() => bookId ? noteRepository.listByBook(bookId) : [], [revision, bookId], []);
};
export const useQuotes = (bookId?: string) => {
  const revision = useActiveDatabaseRevision();
  return useLiveQuery(() => bookId ? quoteRepository.listByBook(bookId) : [], [revision, bookId], []);
};
export const useHistory = (bookId?: string) => {
  const revision = useActiveDatabaseRevision();
  return useLiveQuery(async () => {
  if (!bookId) return { progress: [], statuses: [] };
  const [progress, statuses] = await Promise.all([
    progressRepository.listByBook(bookId), statusHistoryRepository.listByBook(bookId)
  ]);
    return { progress, statuses };
  }, [revision, bookId], { progress: [], statuses: [] });
};
export const useSeries = () => {
  const revision = useActiveDatabaseRevision();
  return useLiveQuery(() => seriesRepository.list(), [revision], []);
};
export const useGoal = (year: number) => {
  const revision = useActiveDatabaseRevision();
  return useLiveQuery(() => goalRepository.getByYear(year), [revision, year]);
};
export const useSettings = () => {
  const revision = useActiveDatabaseRevision();
  const settings = useLiveQuery(() => settingsRepository.get(), [revision]);
  useEffect(() => {
    if (!settings || settings.theme === 'system') void settingsService.ensureInitialized();
  }, [revision, settings]);
  return settings;
};
export const useHomeHighlightContent = () => {
  const revision = useActiveDatabaseRevision();
  const content = useLiveQuery(() => homeHighlightContentRepository.get(), [revision]);
  useEffect(() => {
    const refresh = () => { void homeHighlightService.refresh().catch(() => undefined); };
    const refreshWhenVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    refresh();
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [revision]);
  return content;
};
export const useOpenSession = () => {
  const revision = useActiveDatabaseRevision();
  const session = useLiveQuery(() => sessionRepository.findOpen(), [revision]);
  useEffect(() => { void integrityRepository.recoverOpenSession(); }, [revision]);
  return session;
};

export const useStatistics = (period: StatisticsPeriod, referenceDate?: Date) => {
  const revision = useActiveDatabaseRevision();
  const referenceKey = referenceDate ? `${referenceDate.getFullYear()}-${referenceDate.getMonth()}` : 'current';
  return useLiveQuery(() => statisticsService.get(period, referenceDate), [revision, period, referenceKey]);
};
export const useDailyStatisticsComparison = (referenceDate: Date) => {
  const revision = useActiveDatabaseRevision();
  const year = referenceDate.getFullYear();
  const month = referenceDate.getMonth();
  const referenceKey = `${year}-${month}`;
  return useLiveQuery(async () => {
    const currentReference = new Date(year, month, 1);
    const previousReference = new Date(year, month - 1, 1);
    const [currentPages, previousPages, currentDurationMs, previousDurationMs] = await Promise.all([
      statisticsService.pagesByDay(currentReference),
      statisticsService.pagesByDay(previousReference),
      statisticsService.durationByDay(currentReference),
      statisticsService.durationByDay(previousReference)
    ]);
    return { currentPages, previousPages, currentDurationMs, previousDurationMs };
  }, [revision, referenceKey], {
    currentPages: [],
    previousPages: [],
    currentDurationMs: [],
    previousDurationMs: []
  });
};
export const useBookStatistics = (bookId?: string) => {
  const revision = useActiveDatabaseRevision();
  return useLiveQuery(() => bookId ? statisticsService.getForBook(bookId) : undefined, [revision, bookId]);
};
export const useBookForecast = (book?: Book) => {
  const revision = useActiveDatabaseRevision();
  return useLiveQuery(() => book ? statisticsService.forecast(book) : undefined, [revision, book?.id, book?.updatedAt]);
};

export const useCoverUrl = (coverAssetId?: string): string | undefined => {
  const revision = useActiveDatabaseRevision();
  const asset = useLiveQuery(() => coverAssetId ? coverAssetRepository.get(coverAssetId) : undefined, [revision, coverAssetId]);
  const url = useMemo(() => asset ? URL.createObjectURL(asset.data) : undefined, [asset]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  return url;
};

export const useCurrentReads = (): Book[] => {
  const books = useBooks();
  return useMemo(() => books.filter(({ status }) => status === 'reading').sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt)), [books]);
};
